import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Divider
} from '@mui/material';

export interface AgencyFormData {
  name: string;
  slug: string;
  display_name: string;
  description: string;
  email: string;
  phone: string;
  website: string;
  business_type: string;
  industry: string;
  plan: string;
  max_smbs: number;
  primary_color: string;
  secondary_color: string;
}

interface AgencyFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: AgencyFormData) => void | Promise<void>;
  /**
   * @deprecated Use onSave instead
   */
  onSubmit?: (data: AgencyFormData) => void | Promise<void>;
  /**
   * @deprecated Use onSave instead
   */
  onConfirm?: (data: AgencyFormData) => void | Promise<void>;
  editingAgency?: AgencyFormData | null;
  loading?: boolean;
}

const AgencyFormDialog: React.FC<AgencyFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onSubmit,
  onConfirm,
  editingAgency,
  loading = false
}) => {
  const commitHandler = onSave ?? onSubmit ?? onConfirm;

  useEffect(() => {
    if (!commitHandler) {
      console.error('AgencyFormDialog: no onSave handler provided.');
    }
  }, [commitHandler]);

  const [formData, setFormData] = useState<AgencyFormData>({
    name: '',
    slug: '',
    display_name: '',
    description: '',
    email: '',
    phone: '',
    website: '',
    business_type: 'marketing_agency',
    industry: '',
    plan: 'professional',
    max_smbs: 50,
    primary_color: '#1f2937',
    secondary_color: '#3b82f6'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (editingAgency) {
      setFormData(editingAgency);
    } else {
      setFormData({
        name: '',
        slug: '',
        display_name: '',
        description: '',
        email: '',
        phone: '',
        website: '',
        business_type: 'marketing_agency',
        industry: '',
        plan: 'professional',
        max_smbs: 50,
        primary_color: '#1f2937',
        secondary_color: '#3b82f6'
      });
    }
    setErrors({});
  }, [editingAgency, open]);

  const handleInputChange = (field: keyof AgencyFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from name
    if (field === 'name' && !editingAgency) {
      const slug = value.toString()
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .substring(0, 50);
      setFormData(prev => ({ ...prev, slug }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Agency name is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.business_type) newErrors.business_type = 'Business type is required';
    if (!formData.industry) newErrors.industry = 'Industry is required';
    if (formData.max_smbs < 1) newErrors.max_smbs = 'Max SMBs must be at least 1';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      if (!commitHandler) {
        return;
      }
      await Promise.resolve(commitHandler(formData));
      onClose();
    } catch (error) {
      console.error('Error saving agency:', error);
    }
  };

  const businessTypes = [
    { value: 'marketing_agency', label: 'Marketing Agency' },
    { value: 'consultancy', label: 'Consultancy' },
    { value: 'digital_agency', label: 'Digital Agency' },
    { value: 'creative_agency', label: 'Creative Agency' }
  ];

  const industries = [
    { value: 'hospitality', label: 'Hospitality' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'retail', label: 'Retail' },
    { value: 'technology', label: 'Technology' },
    { value: 'finance', label: 'Finance' },
    { value: 'education', label: 'Education' },
    { value: 'real_estate', label: 'Real Estate' }
  ];

  const plans = [
    { value: 'starter', label: 'Starter' },
    { value: 'professional', label: 'Professional' },
    { value: 'enterprise', label: 'Enterprise' }
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editingAgency ? 'Edit Agency' : 'Create New Agency'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Agency Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Slug"
                value={formData.slug}
                onChange={(e) => handleInputChange('slug', e.target.value)}
                error={!!errors.slug}
                helperText={errors.slug || 'URL-friendly identifier'}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Name"
                value={formData.display_name}
                onChange={(e) => handleInputChange('display_name', e.target.value)}
                helperText="Public display name for white-labeling"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
                helperText="Brief description of the agency"
              />
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Contact Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                helperText="Primary contact phone"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                helperText="Agency website URL"
              />
            </Grid>

            {/* Business Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Business Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.business_type}>
                <InputLabel>Business Type</InputLabel>
                <Select
                  value={formData.business_type}
                  onChange={(e) => handleInputChange('business_type', e.target.value)}
                  label="Business Type"
                >
                  {businessTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.business_type && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.business_type}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.industry}>
                <InputLabel>Industry</InputLabel>
                <Select
                  value={formData.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  label="Industry"
                >
                  {industries.map((industry) => (
                    <MenuItem key={industry.value} value={industry.value}>
                      {industry.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.industry && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.industry}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Plan</InputLabel>
                <Select
                  value={formData.plan}
                  onChange={(e) => handleInputChange('plan', e.target.value)}
                  label="Plan"
                >
                  {plans.map((plan) => (
                    <MenuItem key={plan.value} value={plan.value}>
                      {plan.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max SMBs"
                type="number"
                value={formData.max_smbs}
                onChange={(e) => handleInputChange('max_smbs', parseInt(e.target.value) || 0)}
                error={!!errors.max_smbs}
                helperText={errors.max_smbs || 'Maximum number of SMB clients'}
                inputProps={{ min: 1, max: 1000 }}
              />
            </Grid>

            {/* Branding */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Branding
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Primary Color"
                type="color"
                value={formData.primary_color}
                onChange={(e) => handleInputChange('primary_color', e.target.value)}
                helperText="Primary brand color"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Secondary Color"
                type="color"
                value={formData.secondary_color}
                onChange={(e) => handleInputChange('secondary_color', e.target.value)}
                helperText="Secondary brand color"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || !commitHandler}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Saving...' : editingAgency ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgencyFormDialog;
